<script setup lang="ts">
//#region imports
import { computed, nextTick, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useDetectPermissions } from '@/composables/useDetectPermissions';
import { usePopupManager } from '@/components/PopupManager.vue';
import { useTypedStore } from '@/store';
import UiPagination from '@/components/UIElements/UiPagination.vue';
import { useApi } from '@/api';
import { useTablePaginator, useTableSearch, useTableSort } from '@/composables/useTableLS';
import updateUrl from '@/service/updateUrl';
import _ from '@/setup/lodash';
import { useFormatCurrency } from '@/composables/useFormatCurrency';
import { useLs } from '@/setup/vueLs';
import type { UiTableNewField, UiTableNewSortEmit } from '@/types/table';
import type {
  GetMediaBuyingPaymentsTransfersData,
  GetMediaBuyingPaymentsTransfersResponse,
} from '@/types/openapi-ts';
//#endregion

//#region const
const LS_PATH = 'MediaBuying/PaymentsTransfer';

const sizes = {
  1080: 20,
  1440: 30,
};

const fields: UiTableNewField<GetMediaBuyingPaymentsTransfersResponse['payload'][number]>[]  = [
  {
    name: 'incrementing_id',
    sortable: 'custom',
    width: '60',
  },
  {
    name: 'from_affiliate_email',
    sortable: false,
    link: (row) => getAffiliateLink(row.from_affiliate_id),
    class: 'text',
  },
  {
    name: 'from_campaign_name',
    sortable: false,
    class: 'text',
  },
  {
    name: 'to_affiliate_email',
    sortable: false,
    link: (row) => getAffiliateLink(row.to_affiliate_id),
    class: 'text',
  },
  {
    name: 'to_campaign_name',
    sortable: false,
    class: 'text',
  },
  {
    name: 'to_amount',
    sortable: false,
    format: 'formatMoney',
    currencyKey: 'to_amount_currency_code',
    class: 'text',
  },
  {
    name: 'to_amount_converted',
    sortable: false,
    format: 'formatMoney',
    class: 'text',
  },
  {
    name: 'created_at',
    sortable: 'custom',
    align: 'right',
    width: '120',
    format: 'date-time',
  },
];
//#endregion

//#region composables
const store = useTypedStore();
const route = useRoute();
const api = useApi();
const detectPermissions = useDetectPermissions();
const popupManager = usePopupManager();
const { checkCurrencyCode } = useFormatCurrency();
const ls = useLs();

const { limit, count, page, setPage } = useTablePaginator({
  lsPath: LS_PATH,
  sizes,
  defaultSize: 15,
});

const { sortProp, sortOrder, setSort, optionsSort } = useTableSort({
  initialOptions:{
    sort_column: 'created_at',
    sort_dir: 'desc',
  },
  lsPath: LS_PATH,
  fields,
});

const { searchQuery, setSearch } = useTableSearch();
//#endregion

//#region state
const currencyCode = ref('');

const isDataLoading = ref(false);
const isFirstLoading = ref(true);
const tableData = ref<GetMediaBuyingPaymentsTransfersResponse['payload']>([]);
//#endregion

//#region computed
const adminAcl = computed(() => store.getters['auth/adminAcl']);
const customerCurrencies = computed(() => store.getters['misc/customerCurrencies']);

const canTransfer = computed(() => {
  return detectPermissions.checkRequest('media_buying_payments_review')
    && detectPermissions.checkRequest('affiliates_view');
});
//#endregion

//#region methods
const _query = () => {
  return {
    ...optionsSort.value,
    search: searchQuery.value,
    limit: limit.value,
    offset: limit.value * page.value - limit.value,
    convert_amount_to_currency_code: currencyCode.value,
  } as GetMediaBuyingPaymentsTransfersData['query'];
};

const _completedFilterUrl = () => {
  const _dataFilters = {
    ..._.omit(_query(), ['offset', 'convert_amount_to_currency_code']),
    currency_code: currencyCode.value,
  };
  updateUrl.updateGetParams(_dataFilters);
};

async function fetchData(silent = false) {
  if (!silent && !isFirstLoading.value) {
    isDataLoading.value = true;
  }

  _completedFilterUrl();
  let options = _query();

  try {
    const { data: { payload, misc } } = await api.getMediaBuyingPaymentsTransfers(options);

    count.value = misc!.count;
    currencyCode.value = misc!.currency_code;
    tableData.value = payload;
  } finally {
    if(!isFirstLoading.value) {
      isDataLoading.value = false;
    } else {
      isFirstLoading.value = false;
    }
  }
}

function initData() {
  const _urlData = updateUrl.getParseParams(route.hash);

  if (!_.isEmpty(_urlData)) {
    setPage(_urlData);

    delete _urlData.page;
    delete _urlData.limit;

    _.merge(optionsSort.value, _urlData);

    setSort(_urlData);
    setSearch(_urlData);
    currencyCode.value = checkCurrencyCode(ls.get(`${LS_PATH}/currency_code`));
    delete _urlData.currency_code;
  } else {
    currencyCode.value = checkCurrencyCode(ls.get(`${LS_PATH}/currency_code`));
  }

  nextTick(() => {
    fetchData();
  });
}

function changeCurrency(value: string) {
  currencyCode.value = value;
  ls.set(`${LS_PATH}/currency_code`, value);
  fetchData();
}

function getAffiliateLink(id: string) {
  const isSuperUser = adminAcl.value.is_superuser;
  const hasAffiliatesView = detectPermissions.checkRequest('affiliates_view');

  if (!isSuperUser && !hasAffiliatesView) {
    return '';
  }

  return `/affiliates/${id}/info`;
}

const pageChange: InstanceType<typeof UiPagination>['$props']['onPage-change'] = (pageVal, size) => {
  if (page.value !== pageVal || limit.value !== size) {
    page.value = pageVal;
    limit.value = size;
    fetchData();
  }
};

const handleSort = ({ prop, order }: UiTableNewSortEmit) => {
  if (order === '') return;
  if (sortProp.value !== prop) {
    sortProp.value = prop;
  } else {
    sortOrder.value = order;
  }
  nextTick(() => {
    fetchData();
  });
};

const handleRefresh = () => {
  page.value = 1;
  fetchData();
};

function handleOpenTransferPopup() {
  popupManager.mediaBuyingTransferPopup.open({
    listeners: {
      success: () => {
        fetchData();
      },
    },
  });
}
//#endregion

//#region hooks
initData();
//#endregion
</script>

<template>
  <div class="media-buying-transfer">
    <UiToolbar>
      <template v-slot:title>
        {{ $t('mediaBuying.page_title.media_buying_transfer.name') }}
      </template>
      <template v-slot:left-slot>
        <UiInput
          v-model.trim="searchQuery"
          :width="180"
          is-search
          @search="handleRefresh"
        />
      </template>
      <template v-slot:right-slot>
        <UiCurrencyPicker
          :model-value="currencyCode"
          :currencies="customerCurrencies"
          @update:model-value="changeCurrency"
        />
        <UiPagination
          :page="page"
          :page-size="limit"
          :count="count"
          show-size-select
          @page-change="pageChange"
        />
      </template>
      <template
        v-if="canTransfer"
        v-slot:actions
      >
        <UiButton
          filled
          icon="plus"
          :disabled="isDataLoading"
          @click="handleOpenTransferPopup"
        >
          {{ $t('mediaBuying.transfer.add_transfer') }}
        </UiButton>
      </template>
    </UiToolbar>
    <UiTableNew
      v-loading="isDataLoading"
      :fields="fields"
      :data="tableData"
      :rows-count="limit"
      :currency="currencyCode"
      :sort="{
        prop: sortProp,
        order: sortOrder,
      }"
      lazy-loading
      is-static
      i18n-path="mediaBuying.transfer"
      @sort="handleSort"
    />
  </div>
</template>
